/**
 * Pure aggregation for the Mail preflight size estimate, kept in its own
 * file free of any import back to the Graph/auth stack — preflight.ts's own
 * chain reaches auth/config.ts, which reads import.meta.env at module load
 * time and only exists under Vite, so this couldn't otherwise be unit tested
 * under plain Node.
 *
 * Item count only, deliberately: `sizeInBytes` was flagged as unconfirmed on
 * `mailFolder` in ARCHITECTURE.md Appendix C, and a live tenant confirmed the
 * pessimistic case — Graph v1.0 rejects it outright ("Could not find a
 * property named 'sizeInBytes' on type 'microsoft.graph.mailFolder'"), not
 * just omits it. There is nothing to fall back to per-folder for; it is
 * simply not requested.
 */

export interface MailFolderSizeInfo {
    totalItemCount?: number;
}

export interface SizeEstimate {
    /**
     * Item/message/file count — optional because it isn't every source's
     * natural unit. OneDrive's quota endpoint reports bytes with no item
     * count at all; forcing a fabricated `0` there would render as "0 items,
     * ~45 GB," which reads as wrong rather than as "not tracked."
     */
    items?: number;
    /** Only present when the source actually reported a size. */
    bytes?: number;
}

export function summarizeMailSize(folders: readonly MailFolderSizeInfo[]): SizeEstimate {
    const items = folders.reduce((sum, f) => sum + (f.totalItemCount ?? 0), 0);
    return { items };
}
